import { Breadcrumb, Button, Checkbox, Collapse, DatePicker, Divider, Dropdown, Form, Input, Layout, message, Modal, Skeleton, Space, theme } from 'antd';
import { Content } from 'antd/es/layout/layout';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { apiGetEventCheckoutDetails, apiMarkEventCheckoutAsPaid, apiResendEventCheckoutEmail } from '../../../../api/events';
import './EventCheckoutDetails.scss';

const EventCheckoutDetails = () => {
  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();
  const navigate = useNavigate();
  const { eventID, checkoutID } = useParams();
  const [loading, setLoading] = useState(false);
  const [eventDetails, setEventDetails] = useState({});
  const [form] = Form.useForm();
  const [emailModalVisible, setEmailModalVisible] = useState(false);
  const [emailForm] = Form.useForm();
  const [sendingEmail, setSendingEmail] = useState(false);
  const [markPaidModalVisible, setMarkPaidModalVisible] = useState(false);
  const [markingPaid, setMarkingPaid] = useState(false);
  const [sendTicketsEmail, setSendTicketsEmail] = useState(true);

  const statusTranslations = {
    FREE: "Gratuito",
    EXPIRED: "Expirado",
    PENDING: "Pendente",
    PAID: "Pago",
    CANCELED: "Cancelado"
  };

  const customerEmail = eventDetails?.customer?.email;
  const canBeMarkedAsPaid = eventDetails?.status === 'EXPIRED' || eventDetails?.status === 'PENDING';

  const applyCheckoutDetails = (json) => {
    setEventDetails(json);
    const countryCode = json.customer.country_code || "+55";
    const phoneNumber = json.customer.phone_number || "";
    const displayPhone = phoneNumber ? `${countryCode} ${phoneNumber}` : "";
    form.setFieldsValue({
      name: json.customer.name || "",
      email: json.customer.email || "",
      phone_number: displayPhone,
      startDate: json.created_at ? dayjs(json.created_at) : null,
      status: statusTranslations[json.status] || "",
      used_coupon: json.coupon ? json.coupon.name : "Nenhum cupom utilizado",
    });
  };

  const handleOpenMarkPaidModal = () => {
    setSendTicketsEmail(Boolean(customerEmail));
    setMarkPaidModalVisible(true);
  };

  const handleMarkPaidModalCancel = () => {
    setMarkPaidModalVisible(false);
  };

  const handleMarkPaidModalOk = async () => {
    setMarkingPaid(true);
    try {
      const json = await apiMarkEventCheckoutAsPaid(eventID, checkoutID, sendTicketsEmail);
      applyCheckoutDetails(json);
      setMarkPaidModalVisible(false);
      message.success(sendTicketsEmail
        ? `Compra marcada como paga. Ingressos enviados para ${customerEmail}.`
        : 'Compra marcada como paga.');
    } catch (error) {
      message.error(error.message || 'Erro ao marcar a compra como paga. Tente novamente.');
    } finally {
      setMarkingPaid(false);
    }
  };

  const handleSendToPurchaseEmail = async () => {
    setSendingEmail(true);
    try {
      await apiResendEventCheckoutEmail(eventID, checkoutID);
      message.success(`Ingressos enviados para ${eventDetails?.customer?.email}`);
    } catch (error) {
      message.error(error.message || 'Erro ao reenviar e-mail. Tente novamente.');
    } finally {
      setSendingEmail(false);
    }
  };

  const handleSendToAnotherEmail = () => {
    setEmailModalVisible(true);
  };

  const handleEmailModalOk = async () => {
    try {
      const values = await emailForm.validateFields();
      setSendingEmail(true);

      await apiResendEventCheckoutEmail(eventID, checkoutID, values.email);
      message.success(`Ingressos enviados para ${values.email}`);
      setEmailModalVisible(false);
      emailForm.resetFields();
    } catch (error) {
      if (error.message) {
        message.error(error.message);
      }
      // If no message, it's a validation error - form will show inline errors
    } finally {
      setSendingEmail(false);
    }
  };

  const handleEmailModalCancel = () => {
    setEmailModalVisible(false);
    emailForm.resetFields();
  };

  useEffect(() => {
    apiGetEventCheckoutDetails(eventID, checkoutID).then((json) => {
      applyCheckoutDetails(json);
    })
  }, [eventID, checkoutID])

  return (
    <div className="EventCheckoutDetails">
      <>
        <Layout
          style={{
            padding: "1.25rem",
          }}
        >

          <Content
            style={{
              padding: 24,
              margin: "30px 0 0 0",
              minHeight: 280,
              background: colorBgContainer,
              borderRadius: borderRadiusLG,
            }}
          >
            <Breadcrumb
              style={{
                margin: "16px 0",
              }}
            >
              <div
                style={{
                  display: "flex",
                  width: "100%",
                  justifyContent: "space-between",
                  alignItems: "flex-end",
                }}
              >
                {loading && <Skeleton active paragraph={{ rows: 1 }} />}
                <h2 className="title-page">{eventDetails?.customer?.name}</h2>
                {!loading && (eventDetails?.status === 'PAID' || eventDetails?.status === 'FREE') && (
                  <Dropdown
                    menu={{
                      items: [
                        {
                          key: '1',
                          label: 'Enviar para o e-mail de compra',
                          onClick: handleSendToPurchaseEmail,
                          disabled: sendingEmail,
                        },
                        {
                          key: '2',
                          label: 'Enviar para outro e-mail',
                          onClick: handleSendToAnotherEmail,
                          disabled: sendingEmail,
                        },
                      ],
                    }}
                    trigger={['click']}
                    disabled={sendingEmail}
                  >
                    <Button type="primary" loading={sendingEmail}>
                      Re-enviar ingressos
                    </Button>
                  </Dropdown>
                )}
                {!loading && canBeMarkedAsPaid && (
                  <Button type="primary" onClick={handleOpenMarkPaidModal} loading={markingPaid}>
                    Marcar como pago
                  </Button>
                )}
              </div>
              {loading && <Skeleton active paragraph={{ rows: 0 }} />}
              {!loading && <>
                <Breadcrumb.Item className='goBack' onClick={() => navigate(-1)}>Ticket</Breadcrumb.Item><Breadcrumb.Item className='goBack' onClick={() => navigate(-1)}>Comprador</Breadcrumb.Item>
                <Breadcrumb.Item>{eventDetails?.customer?.name}</Breadcrumb.Item></>}
            </Breadcrumb>
            <div className="container-subtitle">
              {loading && <Skeleton active paragraph={{ rows: 1 }} />}
              {!loading && <h3 className="subtitle-page">1. Infos do Comprador</h3>}
            </div>
            <Divider />
            {loading && <Skeleton active paragraph={{ rows: 3 }} />}
            {!loading && <div className="container-inputs">
              <div className="container-left">
                <Form form={form} layout="vertical">
                  <Form.Item
                    label="Nome do comprador"
                    name="name"
                  >
                    <Input
                      disabled={true}
                    />
                  </Form.Item>
                  <Form.Item
                    label="Email do comprador"
                    name="email"
                  >
                    <Input
                      disabled={true}
                    />
                  </Form.Item>
                  <Form.Item
                    label="Status"
                    name="status"
                  >
                    <Input
                      disabled={true}
                    />
                  </Form.Item>
                </Form>
              </div>
              <div className="container-right">
                <Form form={form} layout="vertical">
                  <Form.Item
                    label="Data da compra"
                    name="startDate"
                    rules={[
                      {
                        required: true,
                        message: "Por favor, selecione a data de início.",
                      },
                    ]}
                  >
                    <DatePicker
                      showTime
                      disabled={true}
                    />
                  </Form.Item>
                  <Form.Item
                    label="Telefone"
                    name="phone_number"
                  >
                    <Input
                      disabled={true}
                    />
                  </Form.Item>
                  <Form.Item
                    label="Cupom"
                    name="used_coupon"
                  >
                    <Input
                      disabled={true}
                    />
                  </Form.Item>
                </Form>
              </div>
            </div>
            }
          </Content>

          {!loading && < Content
            style={{
              padding: 24,
              margin: "30px 0 0 0",
              minHeight: 280,
              background: colorBgContainer,
              borderRadius: borderRadiusLG,
            }}
          >
            <div className="container-subtitle">
              {loading && <Skeleton active paragraph={{ rows: 1 }} />}
              {!loading && <h3 className="subtitle-page">1. Tickets Comprados</h3>}
            </div>
            <Divider />
            {loading && <Skeleton active paragraph={{ rows: 3 }} />}
            {/* TICKETS */}

            <Collapse>
              {eventDetails?.tickets?.map((ticket, index) => (
                <Collapse.Panel
                  header={
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span>{`Ingresso ${index + 1}: ${ticket.ticket.name === 'Free' ? 'Gratuito' : ticket.ticket.name}`}</span>
                      <span>{`R$ ${ticket.ticket === "Free" ? "Gratuito" : (ticket.ticket.amount / 100).toFixed(2)}`}</span>
                    </div>
                  }
                  key={ticket.id}
                >
                  {ticket.answers?.length > 0 ? <h4>Respostas:</h4> : <h4 style={{ fontStyle: 'italic', opacity: '0.6' }}>Esse ingresso não possui respostas.</h4>}
                  {ticket.answers?.map((answer, answerIndex) => (
                    <div key={answerIndex}>
                      <p>
                        <strong>{answer.question}: <br /></strong>
                        {answer.answer}
                        {answer.question_removed && (
                          <span style={{ fontStyle: 'italic', opacity: 0.6, marginLeft: 8 }}>
                            (pergunta alterada ou removida do ingresso)
                          </span>
                        )}
                      </p>
                    </div>
                  ))}
                </Collapse.Panel>
              ))}
            </Collapse>
          </Content>}
        </Layout>
      </>

      <Modal
        title="Marcar como pago"
        open={markPaidModalVisible}
        onOk={handleMarkPaidModalOk}
        onCancel={handleMarkPaidModalCancel}
        okText="Confirmar"
        cancelText="Cancelar"
        confirmLoading={markingPaid}
      >
        <Space direction="vertical" size="middle" style={{ display: 'flex' }}>
          <span>
            Confirmar o pagamento da compra de <strong>{eventDetails?.customer?.name}</strong>
            {' '}&mdash; {eventDetails?.tickets?.length || 0} ingresso(s), total de{' '}
            <strong>R$ {((eventDetails?.total || 0) / 100).toFixed(2)}</strong>.
          </span>
          <span style={{ opacity: 0.65 }}>
            A compra passa a contar como venda nas métricas do evento.
          </span>
          <Checkbox
            checked={sendTicketsEmail}
            disabled={!customerEmail}
            onChange={(e) => setSendTicketsEmail(e.target.checked)}
          >
            {customerEmail
              ? `Enviar os ingressos por e-mail para ${customerEmail}`
              : 'Enviar os ingressos por e-mail'}
          </Checkbox>
          {!customerEmail && (
            <span style={{ fontStyle: 'italic', opacity: 0.6 }}>
              Esse comprador não possui e-mail cadastrado, então os ingressos não podem ser enviados.
            </span>
          )}
        </Space>
      </Modal>

      <Modal
        title="Enviar para outro e-mail"
        open={emailModalVisible}
        onOk={handleEmailModalOk}
        onCancel={handleEmailModalCancel}
        okText="Enviar"
        cancelText="Cancelar"
        confirmLoading={sendingEmail}
      >
        <Form
          form={emailForm}
          layout="vertical"
        >
          <Form.Item
            label="E-mail"
            name="email"
            rules={[
              {
                required: true,
                message: 'Por favor, insira um e-mail.',
              },
              {
                type: 'email',
                message: 'Por favor, insira um e-mail válido.',
              },
            ]}
          >
            <Input placeholder="exemplo@email.com" />
          </Form.Item>
        </Form>
      </Modal>
    </div >
  )
}
export default EventCheckoutDetails
